import { KontorError, type ErrorCode } from "@kontor/shared/errors";

/**
 * The HTTP client.
 *
 * Everything the app fetches goes through here so that three things are handled in
 * exactly one place: the access token, the active organisation, and the error
 * envelope. A component never sees a raw `Response`.
 */

let accessToken: string | null = null;
let organisationId: string | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function setOrganisationId(id: string | null): void {
  organisationId = id;
}

export function getOrganisationId(): string | null {
  return organisationId;
}

/** Called when the API reports the session is gone, so the shell can redirect once. */
let onUnauthenticated: (() => void) | null = null;

export function setUnauthenticatedHandler(handler: (() => void) | null): void {
  onUnauthenticated = handler;
}

interface ApiErrorEnvelope {
  error: { code: ErrorCode; message: string; details?: Record<string, unknown>; requestId?: string };
}

export interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** Query parameters; undefined and null entries are dropped. */
  query?: Record<string, string | number | boolean | undefined | null>;
}

export async function api<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { body, query, headers, ...rest } = options;

  const url = new URL(path.startsWith("/") ? path : `/${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value !== undefined && value !== null) url.searchParams.set(key, String(value));
    }
  }

  const requestHeaders = new Headers(headers);
  if (accessToken) requestHeaders.set("Authorization", `Bearer ${accessToken}`);
  if (organisationId) requestHeaders.set("X-Kontor-Organisation", organisationId);

  let payload: BodyInit | undefined;
  if (body instanceof FormData) {
    payload = body;
  } else if (body !== undefined) {
    requestHeaders.set("Content-Type", "application/json");
    payload = JSON.stringify(body);
  }

  const response = await fetch(url, { ...rest, headers: requestHeaders, body: payload });

  if (response.status === 204) return undefined as T;

  const isJson = response.headers.get("content-type")?.includes("application/json") ?? false;

  if (!response.ok) {
    if (response.status === 401) onUnauthenticated?.();

    if (isJson) {
      const envelope = (await response.json()) as ApiErrorEnvelope;
      throw new KontorError(
        envelope.error.code,
        envelope.error.message,
        envelope.error.details ?? {},
      );
    }
    throw new KontorError(
      "internal",
      `Der Server antwortete mit Status ${response.status}.`,
      { status: response.status },
    );
  }

  return isJson ? ((await response.json()) as T) : (undefined as T);
}

export const get = <T>(path: string, query?: RequestOptions["query"]): Promise<T> =>
  api<T>(path, { method: "GET", query });

export const post = <T>(path: string, body?: unknown): Promise<T> =>
  api<T>(path, { method: "POST", body });

export const patch = <T>(path: string, body?: unknown): Promise<T> =>
  api<T>(path, { method: "PATCH", body });

export const del = <T>(path: string): Promise<T> => api<T>(path, { method: "DELETE" });
